import React, { useEffect, useRef, useState, useMemo } from 'react';
import axios from 'axios';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footerbar from './components/Footerbar';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast, ToastContainer } from 'react-toastify';
import ReactModal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import imageCompression from 'browser-image-compression';


// ─── FIX #14: OrgChart script loader moved to a proper singleton ──────────────
// Using a plain object avoids Strict Mode double-invoke issues with module globals.
const orgChartLoader = {
  loaded: false,
  promise: null,
  load(src) {
    if (this.loaded) return Promise.resolve();
    if (this.promise) return this.promise;
    this.promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => { this.loaded = true; resolve(); };
      script.onerror = reject;
      document.body.appendChild(script);
    });
    return this.promise;
  },
};
// ─────────────────────────────────────────────────────────────────────────────

// ─── FIX #6: parseDate is a pure function — define it outside the component ──
const parseDate = (str) => {
  if (!str || str.length < 10) return null;
  const [dd, mm, yyyy] = str.split('-');
  if (!dd || !mm || !yyyy || yyyy.length < 4) return null;
  const d = new Date(`${yyyy}-${mm}-${dd}`);
  return isNaN(d.getTime()) ? null : d;
};
// ─────────────────────────────────────────────────────────────────────────────

const ADULT_RELATIONSHIPS = ['Father', 'Mother', 'Step-Mother', 'Spouse'];

function FamilyTree() {
  const navigate = useNavigate();

  // ─── FIX #15: removed unused loggedInUserId / loggedFamilyAdmin ──────────
  const { isSidebarVisible, showSidebar, toggleSidebar } = useAppLayout();
  const chartContainerRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const queryClient = useQueryClient();
  const getToken = () => localStorage.getItem('token');

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState({
    id: '', name: '', alt_name: '', img: '', gender: '',
    married: '', single: '', single_mother: '', spouse: '', unknown: '',
  });

  const [isChartLoading, setIsChartLoading] = useState(true);
  const [showSpinner, setShowSpinner] = useState(false);

  const openModalWithData = (data) => {
    setSelectedMember({
      id: data.id || '',
      name: data.name || '',
      alt_name: data.alt_name || '',
      img: data.img || '',
      gender: data.gender || '',
      married: data.married || '',
      single: data.single || '',
      single_mother: data.single_mother || '',
      spouse: data.spouse || '',
      unknown: data.unknown || '',
    });
    setModalIsOpen(true);
  };

  // ─── FIX #4: cacheTime → gcTime (React Query v5) ─────────────────────────
  const { data: familyTreeData, isLoading: isLoadingFamilyTree, isError: isErrorFamilyTree } = useQuery({
    queryKey: ['familyTree'],
    queryFn: () =>
      axios
        .get(`${API_URL}/family-tree`, { headers: { Authorization: `Bearer ${getToken()}` } })
        .then((res) => res.data),
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    let timer;
    if (isLoadingFamilyTree || isChartLoading) {
      timer = setTimeout(() => setShowSpinner(true), 500);
    } else {
      setShowSpinner(false);
    }
    return () => clearTimeout(timer);
  }, [isLoadingFamilyTree, isChartLoading]);

  useEffect(() => {
    if (!familyTreeData) { setIsChartLoading(true); return; }
    setIsChartLoading(true);
    orgChartLoader
      .load(`${process.env.PUBLIC_URL}/orgchart/orgchart.js`)
      .then(() => { if (window.OrgChart) initializeOrgChart(familyTreeData); })
      .catch((err) => { console.error('Failed to load OrgChart script:', err); setIsChartLoading(false); });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [familyTreeData]);

  // ─── FIX #8: tree background via React Query (was raw axios + useEffect) ──
  const { data: treeBgData } = useQuery({
    queryKey: ['treeBgColor'],
    queryFn: () =>
      axios.get(`${API_URL}/get-family-tree-background-api`, { withCredentials: true }).then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });
  const treeBgColor = treeBgData?.color_code ?? '#EBF4ED';
  // ─────────────────────────────────────────────────────────────────────────

  const getInitials = (name = '') => {
    const clean = name.replace(/\s*\(.*?\)\s*/g, '').trim();
    const parts = clean.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return (parts[0]?.[0] ?? '?').toUpperCase();
  };

  const makeInitialsAvatar = (name = '', bgColor = '#ED93B1') => {
    const clean = name.replace(/\s*\(.*?\)\s*/g, '').trim();
    const initials = getInitials(clean);
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="86" height="86">
        <circle cx="43" cy="43" r="43" fill="${bgColor}"/>
        <text x="43" y="43" text-anchor="middle" dominant-baseline="central"
          font-family="system-ui, sans-serif" font-size="28" font-weight="600"
          fill="#ffffff" letter-spacing="1">${initials}</text>
      </svg>`.trim();
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  };

  /*const patchData = (rawData) =>
    rawData.map((node) => {
      const isBlue = Array.isArray(node.tags) && node.tags.includes('blue');
      const bgColor = isBlue ? '#85B7EB' : '#ED93B1';
      return {
        ...node,
        img: node.img && node.img.trim() ? node.img : makeInitialsAvatar(node.name ?? '', bgColor),
      };
    });*/

    const patchData = (rawData) =>
      rawData.map((node) => {
        const isFemale = node.gender === 'Female';
        const bgColor = isFemale ? '#ED93B1' : '#85B7EB';
    
        // Drive the node's rendered circle color off gender instead of the raw 'blue' tag.
        // Female -> default 'family_template' (pink ED93B1, same as partner nodes).
        // Male    -> 'family_template_blue' via the 'blue' tag.
        const existingTags = Array.isArray(node.tags) ? node.tags.filter((t) => t !== 'blue') : [];
        const tags = isFemale ? existingTags : [...existingTags, 'blue'];
    
        return {
          ...node,
          tags,
          img: node.img && node.img.trim() ? node.img : makeInitialsAvatar(node.name ?? '', bgColor),
        };
      });

  const initializeOrgChart = (data) => {
    if (!window.OrgChart || !chartContainerRef.current) { setIsChartLoading(false); return; }
    setIsChartLoading(true);

    if (chartInstanceRef.current) {
      chartInstanceRef.current.load(patchData(data));
    } else {
      const isMobile = window.innerWidth <= 768;
      const OrgChart = window.OrgChart;

      OrgChart.templates.family_template = Object.assign({}, OrgChart.templates.ana);
      OrgChart.templates.family_template.size = [86, 86];
      OrgChart.templates.family_template.plus = '';
      OrgChart.templates.family_template.minus = '';
      OrgChart.templates.family_template.rippleRadius = 40;
      OrgChart.templates.family_template.name = '<text style="font-size: 12px; font-weight: 600;" fill="#000000" x="43" y="100" text-anchor="middle">{val}</text>';
      OrgChart.templates.family_template.title = '<text style="font-size: 12px;" fill="#aeaeae" x="43" y="115" text-anchor="middle">{val}</text>';

      const baseImg = '<clipPath id="{randId}"><circle cx="43" cy="43" r="40.5"></circle></clipPath><image preserveAspectRatio="xMidYMid slice" clip-path="url(#{randId})" xlink:href="{val}" x="2" y="2" width="82" height="82"></image>';

      OrgChart.templates.family_template.node = '<circle stroke-width="1" fill="#ED93B1" stroke="#ED93B1" cx="43" cy="43" r="41.5"></circle>';
      OrgChart.templates.family_template.img = baseImg + `
        <circle fill="#ffffff" stroke="#cccccc" stroke-width="1" cx="77" cy="72" r="10"></circle>
        <text x="77" y="77" text-anchor="middle" font-size="16" fill="#ED93B1" font-weight="bold">+</text>`;

      OrgChart.templates.family_template_blue = Object.assign({}, OrgChart.templates.family_template);
      OrgChart.templates.family_template_blue.node = '<circle stroke-width="1" fill="#85B7EB" stroke="#85B7EB" cx="43" cy="43" r="41.5"></circle>';
      OrgChart.templates.family_template_blue.img = baseImg + `
        <circle fill="#ffffff" stroke="#cccccc" stroke-width="1" cx="77" cy="72" r="10"></circle>
        <text x="77" y="77" text-anchor="middle" font-size="16" fill="#85B7EB" font-weight="bold">+</text>`;

      const chart = new OrgChart(chartContainerRef.current, {
        template: 'family_template',
        enableSearch: true,
        levelSeparation: isMobile ? 60 : 100,
        siblingSeparation: isMobile ? 60 : 100,
        scaleInitial: isMobile ? OrgChart.match.boundary : 1,
        scaleMin: 0.5,
        scaleMax: 3,
        nodeBinding: { name: 'name', title: 'title', img: 'img' },
        tags: {
          blue: { template: 'family_template_blue' },
          partner: { template: 'family_template' },
        },
        nodeMouseClick: OrgChart.action.none,
      });

      chart.on('render-link', (sender, args) => {
        if (args.cnode.ppid !== undefined) {
          args.html += '<use xlink:href="#baby" x="' + args.p.xa + '" y="' + args.p.ya + '"/>';
        }
      });

      chart.on('redraw', () => {
        const el = document.querySelector('#tree svg');
        if (el) el.style.touchAction = 'pan-x pan-y';
      });

      chart.on('click', (sender, args) => {
        if (args?.node) {
          const nodeData = sender.get(args.node.id);
          setTimeout(() => openModalWithData(nodeData), 150);
        }
      });

      chart.load(patchData(data));
      chartInstanceRef.current = chart;
    }

    if (chartInstanceRef.current) {
      chartInstanceRef.current.off('ready');
      chartInstanceRef.current.on('ready', () => setIsChartLoading(false));
    }
  };

  // ─── Modal state ──────────────────────────────────────────────────────────
  const [addFamilyModalIsOpen, setAddFamilyModalIsOpen] = useState(false);

  const openAddFamilyModal = () => setAddFamilyModalIsOpen(true);
  const closeAddFamilyModal = () => {
    setAddFamilyModalIsOpen(false);
    resetForm();
  };

  // ─── FIX #7: removed dead state (selectedRelationship, isDeceased, etc.) ─

  const getPrefixOptions = () => {
    const rel = formData.relationship;
    const maleRelations = ['Father', 'Son', 'Brother'];
    const femaleRelations = ['Mother', 'Step-Mother','Daughter', 'Sister', 'Spouse'];
    const neutral = ['Dr', 'Prof', 'Rev'];
    if (maleRelations.includes(rel)) return ['Mr', ...neutral];
    if (femaleRelations.includes(rel)) return ['Mrs', ...neutral];
    return ['Mr', 'Mrs', ...neutral];
  };

  const [spouseList, setSpouseList] = useState([]);
  const [motherList, setMotherList] = useState([]);
  const [hasFather, setHasFather] = useState(false);
  const [hasMother, setHasMother] = useState(false);

  // ─── FIX #5: single useEffect with Promise.all instead of 3 separate ones ─
  useEffect(() => {
    if (!addFamilyModalIsOpen || !selectedMember?.id) return;

    const fetchSpouses = axios.post(`${API_URL}/family-tree-get-spouse-api`, {
      withCredentials: true, selected_id: selectedMember.id,
    }).then((r) => r.data).catch((e) => { console.error('Error fetching spouses:', e); return []; });

    const fetchMothers = axios.post(`${API_URL}/family-tree-get-mother-api`, {
      withCredentials: true, selected_id: selectedMember.id,
    }).then((r) => r.data).catch((e) => { console.error('Error fetching mothers:', e); return []; });

    const fetchParents = axios.post(`${API_URL}/family-tree-check-father-mother-api`, {
      selected_id: selectedMember.id,
    }, { withCredentials: true })
      .then((r) => r.data)
      .catch((e) => { console.error('Error fetching parent info:', e); return { hasFather: false, hasMother: false }; });

    Promise.all([fetchSpouses, fetchMothers, fetchParents]).then(([spouses, mothers, parents]) => {
      setSpouseList(spouses);
      setMotherList(mothers);
      setHasFather(parents.hasFather);
      setHasMother(parents.hasMother);
    });
  }, [addFamilyModalIsOpen, selectedMember?.id]);

  const getRelationshipOptions = () => {
    const { gender, single, single_mother } = selectedMember || {};
    let baseOptions = [];

    if (gender === 'Male' && single_mother === '') {
      baseOptions = ['Father', 'Spouse'];
      if (hasFather) baseOptions.push('Mother');
      if (hasFather && hasMother) baseOptions.push('Step-Mother');
      if (spouseList.length > 0) baseOptions.push('Son', 'Daughter');
    } else if (gender === 'Male' && single_mother === 1) {
      baseOptions.push('Son', 'Daughter');
    } else if (gender === 'Female' && single === 1) {
      baseOptions = ['Father'];
      if (hasFather) baseOptions.push('Mother');
      if (hasFather && hasMother) baseOptions.push('Step-Mother');
    }

    if (hasFather) baseOptions = baseOptions.filter((r) => r !== 'Father');
    if (hasMother) baseOptions = baseOptions.filter((r) => r !== 'Mother');
    if (!(hasFather && hasMother)) baseOptions = baseOptions.filter((r) => r !== 'Step-Mother');
    if (hasFather && hasMother) baseOptions.push('Brother', 'Sister');

    return baseOptions;
  };

  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const [formData, setFormData] = useState({
    name: '', last_name: '', surname: '', gender: '', date_of_birth: '', date_of_death: '',
    phone_number: '', email: '', relationship: '', prefix: '',
    deceased: false, inactive: false, underage: false,
    selected_id: '', mother_id: '',
  });

  useEffect(() => {
    if (selectedMember) {
      setFormData((prev) => ({ ...prev, selected_id: selectedMember.id }));
    }
  }, [selectedMember]);

  const resetForm = () => {
    setFormData({
      name: '', last_name: '', surname: '', gender: '', date_of_birth: '', date_of_death: '',
      phone_number: '', email: '', relationship: '', prefix: '',
      deceased: false, inactive: false, underage: false,
      selected_id: selectedMember?.id || '',
      mother_id: '',
    });
    setImageFile(null);
    setPreviewImage(null);
  };

  // ─── FIX #6: all date validation in a single useMemo ─────────────────────
  const requiresAdult = ADULT_RELATIONSHIPS.includes(formData.relationship);

  const { dobDate, dodDate, dateError, ageError, dobFutureError, dodFutureError } = useMemo(() => {
    const dob = parseDate(formData.date_of_birth);
    // ─── FIX #10: only parse/validate date of death when the field is visible ─
    const dod = formData.deceased ? parseDate(formData.date_of_death) : null;

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const maxAdultDOB = new Date(now.getFullYear() - 18, now.getMonth(), now.getDate());

    return {
      dobDate: dob,
      dodDate: dod,
      dateError: dob && dod && dob > dod ? 'Date of birth cannot be later than date of death.' : null,
      ageError: requiresAdult && dob && dob > maxAdultDOB ? `${formData.relationship} must be at least 18 years old.` : null,
      dobFutureError: dob && dob > now ? 'Date of birth cannot be in the future.' : null,
      dodFutureError: dod && dod > now ? 'Date of death cannot be in the future.' : null,
    };
  }, [formData.date_of_birth, formData.date_of_death, formData.deceased, formData.relationship, requiresAdult]);
  // ─────────────────────────────────────────────────────────────────────────

  const [phoneMessage, setPhoneMessage] = useState('');

  const handlePhoneChange = (e) => {
    let value = e.target.value;

    // ─── FIX #12: clear message when field is empty ───────────────────────
    if (!value) { setPhoneMessage(''); setFormData({ ...formData, phone_number: '' }); return; }

    if (/\s/.test(value)) setPhoneMessage('⚠️ Spaces are not allowed. They were removed automatically.');
    else setPhoneMessage('');

    value = value.replace(/\s+/g, '');
    if (!/^[0-9+]*$/.test(value)) return;

    if (/^7\d{8}$/.test(value)) {
      value = '0' + value;
      setPhoneMessage('ℹ️ Added leading 0 automatically.');
    }

    if (value.length === 1 && value !== '0' && value !== '+') return;

    const digitsCount = value.replace(/\D/g, '').length;
    if (digitsCount > 15) return;

    setFormData({ ...formData, phone_number: value });
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const addMemberMutation = useMutation({
    mutationFn: async (submissionData) => {
      const response = await axios.post(`${API_URL}/family-tree-add-member-api`, submissionData, { withCredentials: true });
      return response.data;
    },
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['familyTree'] });
        closeAddFamilyModal();
        setModalIsOpen(false);
        toast.success('Family member added successfully', { position: 'top-center', autoClose: 3000 });
        resetForm();
      } else {
        toast.error(data.message || 'Failed to add member', { position: 'top-center', autoClose: 3000 });
      }
    },
    onError: (error) => {
      console.error('Error submitting form:', error);
      const backendMsg = error.response?.data?.message || error.message;
      toast.error(backendMsg || 'An error occurred while adding member.', { position: 'top-center', autoClose: 3000 });
      setIsChartLoading(false);
    },
    onSettled: () => setIsSubmitting(false), // ─── FIX #1 (partial): always reset via onSettled ─
  });

  const handleSubmitMemberDetails = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    // ─── FIX #1: every early return now calls setIsSubmitting(false) ──────
    if (!formData.relationship) {
      toast.error('Relationship is required', { position: 'top-center', autoClose: 3000 });
      setIsSubmitting(false);
      return;
    }

    if (!formData.name) {
      toast.error('First name is required', { position: 'top-center', autoClose: 3000 });
      setIsSubmitting(false);
      return;
    }

    const needsMotherId = ['Brother', 'Sister', 'Son', 'Daughter'].includes(formData.relationship);

    if (needsMotherId && !formData.mother_id) {
      toast.error('Mother is required for this relationship', { position: 'top-center', autoClose: 3000 });
      setIsSubmitting(false);
      return;
    }

    const isExemptFromContact = formData.deceased || formData.inactive || formData.underage;
    const hasPhoneOrEmail = formData.phone_number?.trim() || formData.email?.trim();

    if (!isExemptFromContact && !hasPhoneOrEmail) {
      toast.error('Please provide at least a phone number or an email', { position: 'top-center', autoClose: 3000 });
      setIsSubmitting(false);
      return;
    }

    try {
      const submissionData = new FormData();

      for (const key in formData) {
        if (!['deceased', 'inactive', 'underage', 'mother_id'].includes(key)) {
          if (formData[key] !== undefined && formData[key] !== null) {
            submissionData.append(key, formData[key]);
          }
        }
      }
      submissionData.append('deceased', formData.deceased ? '1' : '0');
      submissionData.append('inactive', formData.inactive ? '1' : '0');
      submissionData.append('underage', formData.underage ? '1' : '0');

      if (needsMotherId) submissionData.append('mother_id', formData.mother_id);
      if (selectedMember?.id) submissionData.append('selected_member_id', selectedMember.id);

      if (imageFile) {
        try {
          const targetKB = 100;
          let quality = 0.9;
          let compressedFile = imageFile;
          while (true) {
            const options = { maxSizeMB: targetKB / 1024, initialQuality: quality, useWebWorker: true };
            compressedFile = await imageCompression(imageFile, options);
            if (compressedFile.size / 1024 <= targetKB || quality <= 0.1) break;
            quality -= 0.1;
          }
          submissionData.append('image', compressedFile);
        } catch (err) {
          console.error('Image compression error:', err);
          toast.warn('Image compression failed. Uploading original image.', { position: 'top-center' });
          submissionData.append('image', imageFile);
        }
      }

      // onSettled in the mutation handles setIsSubmitting(false)
      addMemberMutation.mutate(submissionData);
    } catch (error) {
      console.error(error);
      toast.error('An error occurred while submitting', { position: 'top-center' });
      setIsSubmitting(false);
    }
  };

  const { data: treeExpired, isLoading: isLoadingExpiry } = useQuery({
    queryKey: ['userExpiryDetails'],
    queryFn: () =>
      axios.get(`${API_URL}/user-payment-details-api`, { withCredentials: true }).then((r) => r.data),
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  useEffect(() => {
    if (!isLoadingExpiry && treeExpired) {
      setShowSubscribeModal(!!treeExpired.expired);
    }
  }, [treeExpired, isLoadingExpiry]);

  const [loading, setLoading] = useState(false);
  const handleSendNotification = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/sending-notification-subscription-api`, {}, { withCredentials: true });
      toast.success(response.data.message || 'Notification sent successfully, kindly wait', { position: 'top-center', autoClose: 6000 });
    } catch (error) {
      toast.error('Error sending details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // ─── Date formatter helper (shared by both date inputs) ──────────────────
  const formatDateInput = (raw) => {
    let val = raw.replace(/\D/g, '').slice(0, 8);
    let day = val.slice(0, 2);
    let month = val.slice(2, 4);
    let year = val.slice(4, 8);
    if (day.length === 2 && parseInt(day, 10) > 31) day = '31';
    if (month.length === 2 && parseInt(month, 10) > 12) month = '12';
    let formatted = day;
    if (month) formatted += `-${month}`;
    if (year) formatted += `-${year}`;
    return formatted;
  };

  if (isErrorFamilyTree) {
    return <p style={{ textAlign: 'center', color: 'red' }}>Error loading family tree data.</p>;
  }

  // ─── FIX #13: consolidated "show Add Family button" logic ─────────────────
  const showAddFamilyBtn =
    selectedMember.spouse === '' && (selectedMember.single === 1 || selectedMember.single === '');

  const showExtendedFamilyBtn = selectedMember.spouse !== '' && selectedMember.single === '';

  return (
    <div className="scrollPageContainer theme-light">
      {/* ─── FIX #9: ToastContainer at page root, NOT inside the modal ──── */}
      <ToastContainer />

      <div className="main-wrapper">
        {isSidebarVisible && <Sidebar />}
        <Header toggleSidebar={toggleSidebar} />
        <Sidebar show={showSidebar} toggle={toggleSidebar} />
        <Footerbar toggleSidebar={toggleSidebar} />

        <div className="page-wrapper" style={{ zIndex: 500 }}>
          <div className="content container-fluid" style={{ zIndex: 500 }}>

            <div
              id="tree"
              ref={chartContainerRef}
              style={{
                width: '100%',
                height: '650px',
                backgroundColor: treeBgColor,
                opacity: showSpinner ? 0 : 1,
                transition: 'opacity 0.5s ease-in-out',
              }}
            />

            {showSpinner && (
              <div className="loadingOverlay" style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                display: 'flex', flexDirection: 'column', justifyContent: 'center',
                alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.8)', zIndex: 1000,
              }}>
                <div className="loadingspinner" />
                <p style={{ textAlign: 'center', color: '#000', marginTop: '10px' }}>Loading tree...</p>
              </div>
            )}

            {/* ── Profile / view modal ─────────────────────────────────── */}
            <ReactModal
              isOpen={modalIsOpen}
              onRequestClose={() => setModalIsOpen(false)}
              className="modern-modal profile-modal"
              overlayClassName="modern-overlay"
              ariaHideApp={false}
            >
              <div className="modal-container">
                <button type="button" className="modal-close-btn" onClick={() => setModalIsOpen(false)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                <div className="profile-image-section">
                  <div className="large-profile-image-wrapper">
                    {selectedMember.img ? (
                      <img src={selectedMember.img} alt={selectedMember.name} className="large-profile-image" />
                    ) : (
                      <div className="large-profile-image-placeholder">
                        <svg width="120" height="120" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                        </svg>
                      </div>
                    )}
                    <div className="image-overlay-info">
                      <h2 className="overlay-name">{selectedMember.name}</h2>
                    </div>
                  </div>
                </div>

                {/* ─── FIX #13: single consolidated Add Family / Extended Family button ─ */}
                <div className="compact-action-buttons">
                  {showAddFamilyBtn && (
                    <button className="btn btn-compact btn-add" onClick={openAddFamilyModal}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Add Family
                    </button>
                  )}

                  {showExtendedFamilyBtn && (
                    <button
                      className="btn btn-compact btn-add"
                      onClick={() => navigate(
                        selectedMember.single === ''
                          ? '/family-tree-spouse'
                          : '/family-tree-sister',
                        { state: { memberId: selectedMember.id } }
                      )}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Extended Family
                    </button>
                  )}

                  <button
                    className="btn btn-compact btn-edit"
                    onClick={() => navigate('/edit-personal-information', { state: { memberId: selectedMember.id } })}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Edit
                  </button>

                  <button
                    className="btn btn-compact btn-profile"
                    onClick={() => navigate('/profile', { state: { memberId: selectedMember.id } })}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    Profile
                  </button>
                </div>
              </div>
            </ReactModal>

            {/* ── Add Family modal ─────────────────────────────────────── */}
            <ReactModal
              isOpen={addFamilyModalIsOpen}
              onRequestClose={closeAddFamilyModal}
              className="Modal2 add-family-modal"
              overlayClassName="modern-overlay"
              ariaHideApp={false}
            >
              <div className="modal-container">
                <div className="text-center my-3 position-relative">
                  <button
                    type="button"
                    className="btn-close position-absolute end-0 me-3"
                    aria-label="Close"
                    onClick={closeAddFamilyModal}
                  />
                  <h5 className="fw-bold mb-1">Add Family Member</h5>
                  <p className="text-muted mb-0">
                    Adding to <span className="fw-semibold text-success">{selectedMember?.name || ''}</span> tree
                  </p>
                </div>

                <div className="modal-body-modern px-3 pt-2 pb-3" style={{ marginTop: '-4%' }}>

                  {/* Image upload */}
                  <div className="image-upload-section mb-2">
                    <div className="image-upload-wrapper" onClick={() => document.getElementById('uploadImageInput').click()}>
                      <div className="image-preview">
                        <img src={previewImage || 'img/avatar_male.png'} alt="Preview" className="preview-img" />
                        <div className="upload-overlay">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        </div>
                      </div>
                      <input
                        id="uploadImageInput"
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setImageFile(file);
                            const reader = new FileReader();
                            reader.onloadend = () => setPreviewImage(reader.result);
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Relationship + Mother select */}
                  <div className="row">
                    <div className="col-6 col-md-6">
                      <label className="form-label-modern small mb-1">Select Relationship</label>
                      <select
                        className="form-input-modern form-control form-control-sm"
                        value={formData.relationship}
                        onChange={(e) => {
                          const rel = e.target.value;
                          setFormData({
                            ...formData,
                            relationship: rel,
                            // ─── FIX #11: clear underage when switching to adult relationship ─
                            underage: ADULT_RELATIONSHIPS.includes(rel) ? false : formData.underage,
                          });
                        }}
                      >
                        <option value="">Choose relationship</option>
                        {getRelationshipOptions().map((relation) => (
                          <option key={relation} value={relation}>{relation}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-6 col-md-6">
                      {(formData.relationship === 'Brother' || formData.relationship === 'Sister') && (
                        <>
                          <label className="form-label-modern small mb-1">Select Mother</label>
                          <select
                            className="form-input-modern form-control form-control-sm"
                            value={formData.mother_id || ''}
                            onChange={(e) => setFormData({ ...formData, mother_id: e.target.value })}
                          >
                            <option value="">Choose mother</option>
                            {motherList.map((mother) => (
                              <option key={mother.id} value={mother.id}>{mother.name}</option>
                            ))}
                          </select>
                        </>
                      )}

                      {/* ─── FIX #2: correct operator precedence ─────────── */}
                      {((formData.relationship === 'Son' || formData.relationship === 'Daughter') && selectedMember.gender === 'Male') && (
                        <>
                          <label className="form-label-modern small mb-1">Select Mother</label>
                          <select
                            className="form-input-modern form-control form-control-sm"
                            value={formData.mother_id || ''}
                            onChange={(e) => setFormData({ ...formData, mother_id: e.target.value })}
                          >
                            <option value="">Choose mother</option>
                            {spouseList.map((spouse) => (
                              <option key={spouse.id} value={spouse.id}>{spouse.name}</option>
                            ))}
                          </select>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Prefix + First Name */}
                  <div className="row">
                    <div className="col-6 col-md-6">
                      <label className="form-label-modern small mb-1">Prefix</label>
                      <select
                        className="form-input-modern form-control form-control-sm"
                        value={formData.prefix}
                        onChange={(e) => setFormData({ ...formData, prefix: e.target.value })}
                      >
                        <option value="">Select prefix</option>
                        {getPrefixOptions().map((prefix) => (
                          <option key={prefix} value={prefix}>{prefix}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-6 col-md-6">
                      <label className="form-label-modern small mb-1">First Name *</label>
                      <input
                        type="text"
                        className="form-input-modern form-control form-control-sm"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Enter first name"
                      />
                    </div>
                  </div>

                  {/* Last Name + Surname */}
                  <div className="row">
                    <div className="col-6 col-md-6">
                      <label className="form-label-modern small mb-1">Last Name</label>
                      <input
                        type="text"
                        className="form-input-modern form-control form-control-sm"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        placeholder="Enter last name"
                      />
                    </div>

                    <div className="col-6 col-md-6">
                      <label className="form-label-modern small mb-1">Surname</label>
                      <input
                        type="text"
                        className="form-input-modern form-control form-control-sm"
                        value={formData.surname}
                        onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                        placeholder="Enter surname"
                      />
                    </div>
                  </div>

                  {/* Status checkboxes */}
                  <div className="form-group full-width mb-2">
                    <label className="form-label-modern small mb-1">Status</label>
                    <div className="checkbox-group">
                      <label className="checkbox-wrapper">
                        <input
                          type="checkbox"
                          className="checkbox-input"
                          checked={formData.deceased}
                          onChange={() => setFormData((p) => ({ ...p, deceased: !p.deceased, inactive: false, underage: false }))}
                        />
                        <span className="checkbox-label">Deceased</span>
                      </label>

                      <label className="checkbox-wrapper">
                        <input
                          type="checkbox"
                          className="checkbox-input"
                          checked={formData.inactive}
                          onChange={() => setFormData((p) => ({ ...p, inactive: !p.inactive, deceased: false, underage: false }))}
                        />
                        <span className="checkbox-label">Inactive</span>
                      </label>

                      {/* ─── FIX #11: hidden when adult relationship selected ─ */}
                      {!requiresAdult && (
                        <label className="checkbox-wrapper">
                          <input
                            type="checkbox"
                            className="checkbox-input"
                            checked={formData.underage}
                            onChange={() => setFormData((p) => ({ ...p, underage: !p.underage, deceased: false, inactive: false }))}
                          />
                          <span className="checkbox-label">Under-age</span>
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Date of Birth + Date of Death */}
                  <div className="row">
                    <div className="col-6 col-md-6">
                      <div className="form-group mb-2">
                        <label className="form-label-modern small mb-1">Date of Birth</label>
                        {/* ─── FIX #3: is-invalid includes ageError & dobFutureError ─ */}
                        <input
                          type="text"
                          className={`form-input-modern form-control form-control-sm ${dateError || ageError || dobFutureError ? 'is-invalid' : ''}`}
                          placeholder="dd-mm-yyyy"
                          value={formData.date_of_birth}
                          onChange={(e) => setFormData({ ...formData, date_of_birth: formatDateInput(e.target.value) })}
                          maxLength={10}
                        />
                        {ageError && <div className="text-danger small mt-1">{ageError}</div>}
                        {dobFutureError && <div className="text-danger small mt-1">{dobFutureError}</div>}
                      </div>
                    </div>

                    <div className="col-6 col-md-6">
                      {formData.deceased && (
                        <div className="form-group mb-2">
                          <label className="form-label-modern small mb-1">Date of Death</label>
                          <input
                            type="text"
                            className={`form-input-modern form-control form-control-sm ${dateError || dodFutureError ? 'is-invalid' : ''}`}
                            placeholder="dd-mm-yyyy"
                            value={formData.date_of_death}
                            onChange={(e) => setFormData({ ...formData, date_of_death: formatDateInput(e.target.value) })}
                            maxLength={10}
                          />
                          {dateError && <div className="text-danger small mt-1">{dateError}</div>}
                          {dodFutureError && <div className="text-danger small mt-1">{dodFutureError}</div>}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Phone + Email (hidden when deceased/inactive/underage) */}
                  {!(formData.deceased || formData.inactive || formData.underage) && (
                    <div className="row">
                      <div className="col-6 col-md-6">
                        <div className="form-group mb-2">
                          <label className="form-label-modern small mb-1">Phone Number</label>
                          <input
                            type="text"
                            className="form-input-modern form-control form-control-sm"
                            value={formData.phone_number}
                            onChange={handlePhoneChange}
                            placeholder="Enter phone number"
                          />
                          {phoneMessage && (
                            <small className="text-warning d-block mt-1">{phoneMessage}</small>
                          )}
                        </div>
                      </div>

                      <div className="col-6 col-md-6">
                        <div className="form-group mb-2">
                          <label className="form-label-modern small mb-1">Email</label>
                          <input
                            type="email"
                            className="form-input-modern form-control form-control-sm"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="Enter email address"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="modal-footer-modern py-2 px-3 d-flex justify-content-between">
                  <button onClick={closeAddFamilyModal} className="btn btn-cancel-modern btn-sm">
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitMemberDetails}
                    className="btn btn-primary-modern btn-sm"
                    disabled={isSubmitting || !!dateError || !!ageError || !!dobFutureError || !!dodFutureError}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="loading-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="3" fill="currentColor">
                            <animate attributeName="r" values="3;5;3" dur="1s" repeatCount="indefinite"/>
                          </circle>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <polyline points="17,21 17,13 7,13 7,21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <polyline points="7,3 7,8 15,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Save Member
                      </>
                    )}
                  </button>
                </div>
              </div>
            </ReactModal>

          </div>
        </div>
      </div>

      <style jsx>{`
        :root {
          --brand: #74BA54;
          --brand-600: #5fa12f;
          --brand-700: #4d8e29;
          --brand-dark: #2C533D;
          --ink: #000000;
          --bg: #E3F2E9;
          --bg-soft: #f4faf6;
        }

        .theme-light, .scrollPageContainer { background: var(--bg); }

        .modern-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.35); backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center;
          z-index: 9999; padding: 20px; opacity: 0;
          animation: fadeIn 0.2s ease-out forwards;
          box-sizing: border-box; overflow-y: auto; min-height: 100vh;
        }
        @keyframes fadeIn { to { opacity: 1; } }

        .modern-modal {
          background: transparent; border: 0; border-radius: 0; box-shadow: none;
          outline: none; position: relative; width: auto; max-width: none;
          max-height: none; overflow: visible; margin: auto;
        }

        .modal-container {
          background: linear-gradient(145deg, #ffffff 0%, var(--bg-soft) 100%);
          border-radius: 24px;
          box-shadow: 0 25px 50px -12px rgba(0,0,0,.25), 0 0 0 1px rgba(255,255,255,.8);
          position: relative; overflow: hidden;
          transform: scale(.95) translateY(20px);
          animation: modalSlideIn .3s ease-out forwards;
          backdrop-filter: blur(16px);
          max-height: calc(100vh - 40px); overflow-y: auto; margin: auto;
        }
        @keyframes modalSlideIn { to { transform: scale(1) translateY(0); } }

        .profile-modal .modal-container { width: 400px; max-width: 90vw; height: 600px; max-height: 85vh; display: flex; flex-direction: column; }
        .profile-image-section { flex: 1; position: relative; overflow: hidden; border-radius: 24px 24px 0 0; }
        .large-profile-image-wrapper { width: 100%; height: 100%; position: relative; background: linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%); }
        .large-profile-image { width: 100%; height: 100%; object-fit: cover; display: block; }
        .large-profile-image-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: rgba(255,255,255,.9); background: linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%); }
        .image-overlay-info { position: absolute; left: 0; right: 0; bottom: 0; background: linear-gradient(transparent, rgba(0,0,0,.75)); padding: 40px 20px 20px; text-align: center; }
        .overlay-name { font-size: 28px; font-weight: 700; margin: 0; color: #fff; text-shadow: 0 2px 8px rgba(0,0,0,.5); }

        .add-family-modal .form-input-modern { height: 36px !important; padding: 0.25rem 0.5rem !important; font-size: 0.85rem !important; }
        .add-family-modal .form-label-modern { margin-bottom: 0.25rem !important; font-size: 0.8rem; }

        .compact-action-buttons {
          padding: 16px 20px; display: flex; gap: 8px;
          background: rgba(248,250,252,.95); backdrop-filter: blur(10px);
          border-radius: 0 0 24px 24px; border-top: 1px solid rgba(226,232,240,.8);
        }
        .btn-compact { flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 10px 8px; border-radius: 10px; font-weight: 600; font-size: 12px; border: none; cursor: pointer; transition: .2s; outline: none; min-height: 44px; }

        .btn-add { background: linear-gradient(135deg, var(--brand-dark) 0%, #0f3320 100%); color: #fff !important; box-shadow: 0 2px 8px rgba(24,74,44,.35); }
        .btn-add svg { color: #fff !important; }
        .btn-add:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(24,74,44,.45); }

        .btn-edit { background: transparent; color: var(--brand-dark); border: 2px solid var(--brand); }
        .btn-edit:hover { background: var(--brand); color: #fff; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(117,180,55,.35); }

        .btn-profile { background: linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%); color: #fff; box-shadow: 0 2px 8px rgba(117,180,55,.35); }
        .btn-profile:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(24,74,44,.45); }

        .modal-close-btn { position: absolute; top: 16px; right: 16px; width: 36px; height: 36px; background: rgba(255,255,255,.95); border: 0; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #475569; transition: .2s; z-index: 10; backdrop-filter: blur(10px); }
        .modal-close-btn:hover { background: #fff; color: #ef4444; transform: scale(1.1); }

        .modal-body-modern { padding: 30px; overflow-y: auto; flex: 1; }

        .image-upload-section { display: flex; justify-content: center; margin-bottom: 32px; }
        .image-upload-wrapper { cursor: pointer; transition: transform .2s; }
        .image-upload-wrapper:hover { transform: scale(1.05); }
        .image-preview { position: relative; width: 110px; height: 110px; }
        .preview-img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; border: 4px solid #e2e8f0; transition: border-color .3s; }
        .image-upload-wrapper:hover .preview-img { border-color: var(--brand); }
        .upload-overlay { position: absolute; bottom: 8px; right: 8px; width: 32px; height: 32px; background: var(--brand-dark); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; box-shadow: 0 2px 8px rgba(24,74,44,.35); transition: transform .2s; }
        .image-upload-wrapper:hover .upload-overlay { transform: scale(1.1); }

        .form-group { display: flex; flex-direction: column; }
        .form-group.full-width { grid-column: 1 / -1; }
        .form-label-modern { font-size: 14px; font-weight: 600; color: #374151; margin-bottom: 8px; display: flex; align-items: center; gap: 4px; }
        .form-input-modern { padding: 12px 16px; border: 2px solid #e2e8f0; border-radius: 12px; font-size: 14px; color: #1e293b; background: #fff; transition: .2s; outline: none; }
        .form-input-modern:focus { border-color: var(--brand); box-shadow: 0 0 0 3px rgba(117,180,55,.2); }
        .form-input-modern::placeholder { color: #9ca3af; }

        .checkbox-group { display: flex; flex-wrap: wrap; gap: 16px; margin-top: 8px; }
        .checkbox-wrapper { display: flex; align-items: center; cursor: pointer; user-select: none; }
        .checkbox-input { width: 18px; height: 18px; margin: 0 8px 0 0; cursor: pointer; accent-color: var(--brand-dark); }
        .checkbox-label { font-size: 14px; color: #374151; font-weight: 500; }

        .modal-footer-modern { padding: 24px 30px; border-top: 1px solid #e2e8f0; display: flex; gap: 12px; justify-content: flex-end; background: #f8fafc; }
        .btn { display: inline-flex; align-items: center; gap: 8px; padding: 12px 20px; border-radius: 12px; font-weight: 600; font-size: 14px; border: none; cursor: pointer; transition: .2s; outline: none; position: relative; overflow: hidden; }
        .btn:disabled { opacity: .6; cursor: not-allowed; }
        .btn-primary-modern { background: linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%); color: #fff; box-shadow: 0 4px 12px rgba(117,180,55,.35); }
        .btn-primary-modern:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(24,74,44,.45); }
        .btn-cancel-modern { background: #f1f5f9; color: #64748b; border: 2px solid #e2e8f0; }
        .btn-cancel-modern:hover { background: #e2e8f0; border-color: #cbd5e1; }

        .loading-spinner { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        .add-family-modal .checkbox-group { display: flex !important; flex-direction: row !important; align-items: center; gap: 24px; flex-wrap: nowrap; white-space: nowrap; }
        .add-family-modal .checkbox-wrapper { display: inline-flex; align-items: center; gap: 8px; }
        .add-family-modal .checkbox-input { width: 16px; height: 16px; margin: 0; accent-color: var(--brand-dark); }

        @media (max-width: 640px) {
          .modal-container { margin: 10px; width: calc(100vw - 20px); }
          .profile-modal .modal-container { height: 500px; }
          .compact-action-buttons { padding: 12px 16px; flex-direction: column; gap: 8px; }
          .btn-compact { width: 100%; font-size: 13px; padding: 12px; }
          .modal-body-modern { padding: 20px; }
          .overlay-name { font-size: 24px; }
          .checkbox-group { flex-direction: column; gap: 12px; }
          .modern-overlay { padding: 10px; align-items: flex-start; padding-top: 20px; }
          .modal-container { margin: 0; width: 100%; max-height: calc(100vh - 40px); }
        }

        @media (max-width: 480px) {
          .compact-action-buttons { padding: 10px 12px; }
          .btn-compact { font-size: 11px; padding: 10px 8px; gap: 4px; }
          .overlay-name { font-size: 20px; }
          .profile-modal .modal-container { height: 450px; }
        }

        :root { --footerbar-height: 64px; }
      `}</style>
    </div>
  );
}

export default FamilyTree;